import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { getFirestore, doc, serverTimestamp, collection, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Upload, Check, AlertTriangle, Loader2, Ship, Truck, Pencil, Trash2, Plus } from 'lucide-react';
import { parseForwarding, detectBrokerReport } from './actualCost2Parse';
import { upsertBrokerInvoiceBatch } from '../services/brokerInvoiceLookup';

/**
 * ActualCost2Uploads — CHA-323 phase 3. Parses the C-Air broker exports client-side, previews
 * counts/conflicts BEFORE any write, then commits:
 *   - Invoice → Job list  → lookup_broker_invoice (Firestore; idempotent merge on the invoice slug).
 *   - Forwarding freight  → BigQuery ac2_freight_by_job (via the phase-3b ingest callable — preview
 *     works now, commit is staged until that callable deploys).
 */

async function readAoa(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' });
}

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function StatPill({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      <span className="font-semibold">{value}</span> {label}
    </span>
  );
}

export default function ActualCost2Uploads({ tenantId }) {
  // Invoice → Job state
  const [ijPreview, setIjPreview] = useState(null);
  const [ijError, setIjError] = useState(null);
  const [ijBusy, setIjBusy] = useState(false);
  const [ijResult, setIjResult] = useState(null);
  const [skipChanged, setSkipChanged] = useState({}); // comm_inv -> true = don't overwrite this existing job#

  // CHA-418 — hand-entered freight (prepaid / vendor-billed / parcel), i.e. freight C-Air never billed.
  // Firestore is the system of record — this is operator CRUD, not analytics (§4.1 / ADR 0006). The
  // reconciliation reads it UNDER the C-Air figure, so an entry here can only ever fill a gap.
  const [mfRows, setMfRows] = useState(null);      // null = not loaded yet
  const [mfOpen, setMfOpen] = useState(false);
  const [mfJob, setMfJob] = useState('');
  const [mfOcean, setMfOcean] = useState('');
  const [mfAir, setMfAir] = useState('');
  const [mfReason, setMfReason] = useState('');
  const [mfNote, setMfNote] = useState('');
  const [mfEditing, setMfEditing] = useState(null); // doc id being edited, or null for a new entry
  const [mfBusy, setMfBusy] = useState(false);
  const [mfError, setMfError] = useState(null);
  const [mfResult, setMfResult] = useState(null);

  // Freight state
  const [frPreview, setFrPreview] = useState(null);
  const [frError, setFrError] = useState(null);
  const [frBusy, setFrBusy] = useState(false);
  const [frResult, setFrResult] = useState(null);

  const onInvoiceJobFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIjError(null); setIjResult(null); setIjPreview(null); setSkipChanged({});
    try {
      // CHA-370 — multi-format: sniff the report type from its header signature and route.
      const aoa = await readAoa(file);
      const reportType = detectBrokerReport(aoa);
      if (!reportType) throw new Error('Unrecognized broker report — no known header signature (Invoice Line / Declarations). New format? Send the file to be added to the registry.');
      if (reportType.type === 'declarations') {
        const parsedDecl = reportType.parse(aoa);
        setIjPreview({ reportType: 'declarations', fileName: file.name, decl: parsedDecl });
        return;
      }
      const parsed = reportType.parse(aoa);
      // Compare against the current lookup so the operator sees new vs overwrite BEFORE committing.
      const snap = await getDocs(collection(getFirestore(), 'lookup_broker_invoice'));
      const existing = new Map();
      snap.forEach((d) => { const x = d.data(); if (x.commercial_invoice) existing.set(String(x.commercial_invoice).trim().toUpperCase(), String(x.job_number || '')); });
      const isNew = [], unchanged = [], changed = [];
      for (const r of parsed.records) {
        const cur = existing.get(r.commercial_invoice.trim().toUpperCase());
        if (cur === undefined) isNew.push(r);
        else if (cur === r.job_number) unchanged.push(r);
        else changed.push({ ...r, old_job: cur });
      }
      setIjPreview({ ...parsed, fileName: file.name, isNew, unchanged, changed });
    } catch (err) {
      setIjError(err.message);
    }
  };

  const commitInvoiceJob = async () => {
    if (!ijPreview) return;
    // CHA-370 — declarations path: store job→container pairs, then run the container bridge
    // (auto-seeds unique invoice→job links; ambiguous ones come back as suggestions).
    if (ijPreview.reportType === 'declarations') {
      setIjBusy(true); setIjResult(null); setIjError(null);
      try {
        const fns = getFunctions();
        const ing = await httpsCallable(fns, 'ingestAc2JobContainers', { timeout: 150000 })({ rows: ijPreview.decl.rows, source: ijPreview.fileName });
        // Pairs are stored at this point — a bridge failure must not read as "nothing happened" (review P3).
        try {
          const bridge = await httpsCallable(fns, 'runContainerBridge', { timeout: 150000 })({});
          setIjResult({ declarations: ing.data, bridge: bridge.data });
        } catch (bridgeErr) {
          setIjResult({ declarations: ing.data, bridge: null });
          setIjError(`Pairs stored (${ing.data.upserted}), but the container bridge failed: ${bridgeErr.message} — retry via another upload.`);
        }
      } catch (err) {
        setIjError(`Commit failed: ${err.message}`);
      } finally {
        setIjBusy(false);
      }
      return;
    }
    // Write only what actually changes: new pairs + overwrites the operator didn't skip. Unchanged
    // pairs are no-ops (skipped). Held conflicts + skipped overwrites are never written.
    const recs = [
      ...ijPreview.isNew,
      ...ijPreview.changed.filter((c) => !skipChanged[c.commercial_invoice]),
    ];
    if (!recs.length) { setIjResult({ upserted: 0, unchanged: ijPreview.unchanged.length }); return; }
    setIjBusy(true); setIjResult(null); setIjError(null);
    let acknowledged = 0;
    try {
      for (let i = 0; i < recs.length; i += 400) {
        // `note` is intentionally omitted server-side so operator edits survive the merge.
        const batch = recs.slice(i, i + 400);
        const result = await upsertBrokerInvoiceBatch(tenantId, batch, ijPreview.fileName);
        const upserted = Number(result?.upserted);
        if (upserted !== batch.length) {
          throw new Error(`Broker gateway acknowledged ${Number.isFinite(upserted) ? upserted : 0} of ${batch.length} rows in the current batch.`);
        }
        acknowledged += upserted;
      }
      setIjResult({ upserted: acknowledged, unchanged: ijPreview.unchanged.length, skipped: ijPreview.changed.filter((c) => skipChanged[c.commercial_invoice]).length });
    } catch (err) {
      setIjResult({
        upserted: acknowledged,
        unchanged: ijPreview.unchanged.length,
        skipped: ijPreview.changed.filter((c) => skipChanged[c.commercial_invoice]).length,
        unacknowledgedRows: recs.slice(acknowledged),
        partial: true,
      });
      setIjError(`Commit failed after ${acknowledged} of ${recs.length} records were acknowledged: ${err.message}`);
    } finally {
      setIjBusy(false);
    }
  };

  const changedToWrite = ijPreview && ijPreview.changed ? ijPreview.changed.filter((c) => !skipChanged[c.commercial_invoice]).length : 0;
  const toCommit = ijPreview && ijPreview.isNew ? ijPreview.isNew.length + changedToWrite : 0;

  const onFreightFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFrError(null); setFrPreview(null); setFrResult(null);
    try {
      const parsed = parseForwarding(await readAoa(file));
      setFrPreview({ ...parsed, fileName: file.name });
    } catch (err) {
      setFrError(err.message);
    }
  };

  // ── CHA-418: hand-entered freight, straight to Firestore ──────────────────────────────────────────
  // Doc id IS the normalised job, so re-entering the same job edits it rather than creating a twin the
  // mirror would then have to disambiguate. Normalisation matches the backend's normJob exactly: a
  // B00310630/A entry must land on the same key as the C-Air row, or the view's GROUP BY sums both.
  const normManJob = (x) => String(x || '').trim().toUpperCase().split(/[/ ]/)[0];

  const loadManualFreight = async () => {
    try {
      const snap = await getDocs(collection(getFirestore(), 'lookup_manual_freight'));
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      rows.sort((a, b) => String(a.job_number || '').localeCompare(String(b.job_number || '')));
      setMfRows(rows);
    } catch (e) {
      setMfError(`Could not load hand-entered freight: ${e.message}`);
      setMfRows([]); // render the panel rather than a permanent spinner — the error above says why
    }
  };

  const resetManualForm = () => {
    setMfJob(''); setMfOcean(''); setMfAir(''); setMfReason(''); setMfNote(''); setMfEditing(null);
  };

  const saveManualFreight = async () => {
    setMfBusy(true); setMfError(null); setMfResult(null);
    try {
      const job = normManJob(mfJob);
      // Both shapes that exist in the broker data: C-Air's own B-series, and a plain 6–10 digit broker
      // reference (1479123 is one). Profiled against imports_data_source_1 — nothing else occurs.
      if (!/^B\d+$/.test(job) && !/^\d{6,10}$/.test(job)) {
        setMfError('Job number must be a C-Air job (e.g. B00342849) or a 6–10 digit broker reference (e.g. 1479123).');
        return;
      }
      const num = (v) => (String(v).trim() === '' ? null : Number(v));
      const ocean = num(mfOcean); const air = num(mfAir);
      if ((ocean !== null && !Number.isFinite(ocean)) || (air !== null && !Number.isFinite(air))) {
        setMfError('Freight amounts must be numbers — digits only, no commas or $.');
        return;
      }
      if (ocean === null && air === null) {
        setMfError('Enter at least one of ocean or air freight.');
        return;
      }
      if (!mfReason) { setMfError('Choose why this freight is being entered by hand.'); return; }
      if (mfReason === 'Other' && !mfNote.trim()) { setMfError('Describe the reason — "Other" needs an explanation.'); return; }
      await setDoc(doc(getFirestore(), 'lookup_manual_freight', job), {
        job_number: job,
        ocean_freight: ocean,
        air_freight: air,
        reason: mfReason,
        note: mfNote.trim(),
        updated_at: serverTimestamp(),
      }, { merge: true });
      setMfResult(`Saved freight for ${job}.`);
      resetManualForm();
      await loadManualFreight();
    } catch (e) {
      setMfError(`Not saved — ${e.message}`);
    } finally {
      setMfBusy(false);
    }
  };

  const deleteManualFreight = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the hand-entered freight for ${row.job_number}? The reconciliation will fall back to whatever C-Air billed (often nothing).`)) return;
    setMfBusy(true); setMfError(null); setMfResult(null);
    try {
      await deleteDoc(doc(getFirestore(), 'lookup_manual_freight', row.id));
      // If this was the row under edit, the form still points at a doc that no longer exists — and the
      // button still says "Update this job", so pressing it would RE-CREATE what was just removed.
      if (mfEditing === row.id) resetManualForm();
      setMfResult(`Removed ${row.job_number}.`);
      await loadManualFreight();
    } catch (e) {
      setMfError(`Not removed — ${e.message}`);
    } finally {
      setMfBusy(false);
    }
  };

  const editManualFreight = (row) => {
    setMfEditing(row.id);
    setMfJob(row.job_number || '');
    setMfOcean(row.ocean_freight === null || row.ocean_freight === undefined ? '' : String(row.ocean_freight));
    setMfAir(row.air_freight === null || row.air_freight === undefined ? '' : String(row.air_freight));
    setMfReason(row.reason || '');
    setMfNote(row.note || '');
    setMfResult(null); setMfError(null);
  };

  const commitFreight = async () => {
    if (!frPreview?.rows?.length) return;
    setFrBusy(true); setFrResult(null); setFrError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'ingestAc2Freight', { timeout: 150000 });
      const res = await fn({ rows: frPreview.rows, source: frPreview.fileName });
      // CHA-370 — freight carries containers too: refresh the container bridge after ingest.
      let bridge = null; let bridgeError = null;
      try { bridge = (await httpsCallable(getFunctions(), 'runContainerBridge', { timeout: 150000 })({})).data; }
      catch (e) { bridgeError = e.message; }
      setFrResult({ ...res.data, bridge, bridgeError });
    } catch (err) {
      // CHA-420 — a TOTAL refusal throws, so the reasons ride in `details` rather than a resolved value.
      // Surface them into the same skip panel: "Commit failed: invalid-argument" names nothing.
      const d = err && err.details;
      if (d && Array.isArray(d.skippedRows) && d.skippedRows.length) {
        setFrResult({ upserted: 0, skipped: d.skipped ?? d.skippedRows.length, skippedRows: d.skippedRows });
      }
      setFrError(`Commit failed: ${err.message}`);
    } finally {
      setFrBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Invoice → Job */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <Ship className="h-4 w-4 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-800 m-0">Broker report (Invoice → Job)</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Accepts either C-Air report (format auto-detected): <span className="font-medium">Invoice Line Report</span> — direct invoice→job upsert into <code className="text-[11px]">lookup_broker_invoice</code> (operator edits preserved) — or <span className="font-medium">Declarations Report</span> — job→container pairs feeding the container bridge, which auto-links invoices via the received containers. More formats can be added to the registry.</p>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 cursor-pointer">
          <Upload className="h-4 w-4" /> Choose file
          <input type="file" accept=".xlsx,.xls" onChange={onInvoiceJobFile} className="hidden" />
        </label>

        {ijError && <p className="mt-2 text-sm text-red-700 inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{ijError}</p>}

        {/* CHA-370 — Declarations report preview + commit (job→container pairs → bridge) */}
        {ijPreview && ijPreview.reportType === 'declarations' && (
          <div className="mt-3 space-y-3">
            <div className="text-xs text-slate-500">{ijPreview.fileName} · detected: <span className="font-medium">Declarations Report</span></div>
            <div className="flex flex-wrap gap-2">
              <StatPill value={ijPreview.decl.stats.distinctJobs} label="jobs" />
              <StatPill value={ijPreview.decl.stats.containerPairs} label="container pairs" tone="green" />
              <StatPill value={ijPreview.decl.stats.jobsWithContainers} label="jobs with containers" />
            </div>
            {ijPreview.decl.stats.containerPairs === 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">No containers in this file (air shipments carry none) — nothing to bridge. Export without the AIR transport filter to include ocean entries.</p>
            )}
            <div className="flex items-center gap-3">
              <button onClick={commitInvoiceJob} disabled={ijBusy}
                className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded">
                {ijBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {ijBusy ? 'Committing…' : 'Store pairs + run container bridge'}
              </button>
              {ijResult && ijResult.bridge && (
                <span className="text-sm text-green-700 inline-flex items-center gap-1"><Check className="h-4 w-4" />
                  {ijResult.declarations.upserted} pairs stored · bridge linked {ijResult.bridge.stats.seeds} invoices ({ijResult.bridge.stats.ambiguous} ambiguous, {ijResult.bridge.stats.alreadyLinked} already linked)
                </span>
              )}
            </div>
            {ijResult && ijResult.bridge && ijResult.bridge.ambiguous.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                <div className="font-semibold mb-1">Ambiguous — container touches more than one job (pick manually in the Links tab):</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {ijResult.bridge.ambiguous.slice(0, 10).map((a) => (
                    <li key={a.commercial_invoice}><span className="font-mono">{a.commercial_invoice}</span> → {a.jobs.join(' or ')} <span className="text-slate-500">(via {a.containers})</span></li>
                  ))}
                  {ijResult.bridge.ambiguous.length > 10 && <li>…and {ijResult.bridge.ambiguous.length - 10} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {ijPreview && !ijPreview.reportType && (
          <div className="mt-3 space-y-3">
            <div className="text-xs text-slate-500">{ijPreview.fileName} · detected: <span className="font-medium">Invoice Line Report</span></div>
            <div className="flex flex-wrap gap-2">
              <StatPill value={ijPreview.stats.distinctInvoices} label="invoices" />
              <StatPill value={ijPreview.isNew.length} label="new" tone="green" />
              <StatPill value={ijPreview.unchanged.length} label="unchanged (skipped)" />
              {ijPreview.changed.length > 0 && <StatPill value={ijPreview.changed.length} label="would overwrite" tone="amber" />}
              {ijPreview.conflicts.length > 0 && <StatPill value={ijPreview.conflicts.length} label="conflicts (held)" tone="amber" />}
              {ijPreview.collisions.length > 0 && <StatPill value={ijPreview.collisions.length} label="slug collisions (held)" tone="red" />}
              {ijPreview.invalidJobs.length > 0 && <StatPill value={ijPreview.invalidJobs.length} label="invalid job# (held)" tone="red" />}
            </div>

            {ijPreview.invalidJobs.length > 0 && (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900" aria-label="invalid broker job rows">
                <div className="font-semibold mb-1">Held — job number is outside the broker lookup keyspace:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {ijPreview.invalidJobs.map((r) => (
                    <li key={`${r.row}-${r.commercial_invoice}`}>Row {r.row}: <span className="font-mono">{r.commercial_invoice}</span> → <span className="font-mono">{r.job_number}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {ijPreview.changed.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                <div className="font-semibold mb-1">These invoices already have a different job# — check to SKIP (keep the existing value, e.g. a manual fix):</div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="min-w-full">
                    <tbody>
                      {ijPreview.changed.map((c) => (
                        <tr key={c.commercial_invoice}>
                          <td className="py-0.5 pr-2"><input type="checkbox" checked={!!skipChanged[c.commercial_invoice]} onChange={(e) => setSkipChanged((m) => ({ ...m, [c.commercial_invoice]: e.target.checked }))} /></td>
                          <td className="py-0.5 pr-2 font-mono">{c.commercial_invoice}</td>
                          <td className="py-0.5 pr-2 text-slate-500">{c.old_job} → <span className="font-medium text-amber-900">{c.job_number}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {ijPreview.conflicts.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                <div className="font-semibold mb-1">Held — invoice maps to more than one job (needs broker confirmation):</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {ijPreview.conflicts.slice(0, 10).map((c) => (
                    <li key={c.commercial_invoice}><span className="font-mono">{c.commercial_invoice}</span> → {c.jobs.join(', ')}</li>
                  ))}
                  {ijPreview.conflicts.length > 10 && <li>…and {ijPreview.conflicts.length - 10} more</li>}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={commitInvoiceJob}
                disabled={ijBusy || toCommit === 0}
                className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded"
              >
                {ijBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {ijBusy ? 'Committing…' : `Commit ${toCommit} (${ijPreview.isNew.length} new, ${changedToWrite} overwrite)`}
              </button>
              {ijResult && !ijResult.partial && <span className="text-sm text-green-700 inline-flex items-center gap-1"><Check className="h-4 w-4" />Wrote {ijResult.upserted} pairs — {ijResult.unchanged} unchanged, {ijResult.skipped} skipped.</span>}
            </div>
            {ijResult?.partial && (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900" aria-label="unacknowledged broker rows">
                <div className="font-semibold mb-1">Acknowledged {ijResult.upserted} row(s). Unacknowledged rows:</div>
                <ul className="list-disc pl-5 space-y-0.5 max-h-48 overflow-y-auto">
                  {ijResult.unacknowledgedRows.map((r) => (
                    <li key={r.commercial_invoice}><span className="font-mono">{r.commercial_invoice}</span> → <span className="font-mono">{r.job_number}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Freight */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <Truck className="h-4 w-4 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-800 m-0">Freight (Forwarding Invoices)</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">C-Air "List of Forwarding Invoices" — job# → ocean + air freight (+ container). Lands in BigQuery <code className="text-[11px]">ac2_freight_by_job</code>.</p>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 cursor-pointer">
          <Upload className="h-4 w-4" /> Choose file
          <input type="file" accept=".xlsx,.xls" onChange={onFreightFile} className="hidden" />
        </label>

        {frError && <p className="mt-2 text-sm text-red-700 inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{frError}</p>}

        {frPreview && (
          <div className="mt-3 space-y-3">
            <div className="text-xs text-slate-500">{frPreview.fileName}</div>
            <div className="flex flex-wrap gap-2">
              <StatPill value={frPreview.stats.distinctJobs} label="jobs" />
              <StatPill value={money(frPreview.stats.totalOcean)} label="ocean" />
              <StatPill value={money(frPreview.stats.totalAir)} label="air" />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={commitFreight}
                disabled={frBusy || !frPreview.rows.length}
                className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded"
              >
                {frBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {frBusy ? 'Committing…' : `Commit ${frPreview.rows.length} jobs to BigQuery`}
              </button>
              {/* CHA-420 — a TOTAL refusal now populates frResult so its per-row reasons can render, so this
                  success line must ALSO require that something was actually written. A green ✓ reading
                  "Upserted 0 jobs" beside the red failure is precisely the mistaken-for-success the ticket
                  exists to remove. */}
              {frResult && frResult.upserted > 0 && (
                <span className="text-sm text-green-700 inline-flex items-center gap-1">
                  <Check className="h-4 w-4" />Upserted {frResult.upserted} jobs{frResult.skipped ? ` (${frResult.skipped} skipped)` : ''}{frResult.bridge ? ` · bridge linked ${frResult.bridge.stats.seeds} invoices (${frResult.bridge.stats.ambiguous} ambiguous)` : ''}{frResult.bridgeError ? ` · bridge FAILED: ${frResult.bridgeError}` : ''}.
                </span>
              )}
            </div>
            {/* CHA-420 — NAME what was refused. A bare "N skipped" is how two live BL shipments
                (1479123 / 1480295, $21,099 of system freight) failed invisibly for weeks. Two sources and
                BOTH must show: the parser drops non-B-series rows before the server ever sees them (so a
                server-only list would still have hidden those two jobs), and the server refuses what
                reaches it. Parser skips render at PREVIEW time — before committing — so the operator can
                act on them rather than learning after the fact. */}
            {(() => {
              const parseSkips = (frPreview && frPreview.skippedRows) || [];
              const serverSkips = (frResult && frResult.skippedRows) || [];
              const listed = [...parseSkips, ...serverSkips];
              if (!listed.length) return null;
              // Counts come from the truthful totals; both lists are capped at 50, so state the gap rather
              // than showing a capped length as if it were the count.
              const parseTotal = (frPreview && frPreview.skipped) ?? parseSkips.length;
              const total = parseTotal + ((frResult && frResult.skipped) ?? serverSkips.length);
              return (
                <div role="alert" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" aria-label="skipped freight rows">
                  <div className="font-medium inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Not recorded ({total}):</div>
                  <ul className="mt-1 space-y-0.5">
                    {listed.map((s, i) => (
                      <li key={`${s.job_number}-${i}`}><strong>{s.job_number}</strong> — {s.reason}</li>
                    ))}
                  </ul>
                  {total > listed.length && (
                    <div className="mt-1 italic">…and {total - listed.length} more not listed.</div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* CHA-418 — freight C-Air never billed. Stored in Firestore (operator CRUD, §4.1/ADR 0006) and
            read UNDER the C-Air figure, so an entry here fills a gap and never displaces the forwarder's
            number. Editable and removable, unlike the upload path. */}
        <div className="mt-5 pt-4 border-t border-slate-200">
          <button type="button" aria-label="toggle manual freight"
            onClick={() => { const next = !mfOpen; setMfOpen(next); if (next && mfRows === null) loadManualFreight(); }}
            className="text-sm font-medium text-teal-700 hover:text-teal-800">
            {mfOpen ? '− Hide freight entered by hand' : '+ Freight C-Air never billed (prepaid / vendor / parcel)'}
          </button>

          {mfOpen && (
            <div className="mt-3 space-y-3" aria-label="manual freight panel">
              <p className="text-xs text-slate-500 m-0">
                For shipments C-Air never invoiced. Leave an amount blank to record only the other one.
                Freight allocates <strong>by volume</strong> across every style on the job. If C-Air later
                bills this job <strong>any non-zero amount</strong>, their figure wins and this one stops being used
                — a C-Air line of exactly zero does not displace it.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <label className="text-xs text-slate-600">
                  <span className="block mb-0.5">Job # or broker ref</span>
                  <input value={mfJob} onChange={(e) => { setMfJob(e.target.value); setMfError(null); setMfResult(null); }}
                    disabled={!!mfEditing} placeholder="B00342849 or 1479123" aria-label="manual job number"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm disabled:bg-slate-100" />
                </label>
                <label className="text-xs text-slate-600">
                  <span className="block mb-0.5">Ocean freight</span>
                  <input value={mfOcean} onChange={(e) => { setMfOcean(e.target.value); setMfError(null); setMfResult(null); }}
                    placeholder="blank = none" aria-label="manual ocean freight"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                </label>
                <label className="text-xs text-slate-600">
                  <span className="block mb-0.5">Air freight</span>
                  <input value={mfAir} onChange={(e) => { setMfAir(e.target.value); setMfError(null); setMfResult(null); }}
                    placeholder="blank = none" aria-label="manual air freight"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                </label>
                <label className="text-xs text-slate-600">
                  <span className="block mb-0.5">Reason <span className="text-red-600">*</span></span>
                  <select value={mfReason} onChange={(e) => { setMfReason(e.target.value); setMfError(null); setMfResult(null); }}
                    aria-label="manual freight reason"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white">
                    <option value="">Choose…</option>
                    <option value="Prepaid">Prepaid</option>
                    <option value="Vendor">Billed by vendor</option>
                    <option value="Parcel">Parcel</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
              </div>

              {mfReason === 'Other' && (
                <label className="text-xs text-slate-600 block">
                  <span className="block mb-0.5">Explain <span className="text-red-600">*</span></span>
                  <input value={mfNote} onChange={(e) => { setMfNote(e.target.value); setMfError(null); setMfResult(null); }}
                    aria-label="manual freight note" placeholder="why this freight is being entered by hand"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                </label>
              )}

              <div className="flex items-center gap-3">
                <button onClick={saveManualFreight} disabled={mfBusy || !mfJob.trim() || !mfReason}
                  className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded">
                  {mfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {mfBusy ? 'Saving…' : (mfEditing ? 'Update this job' : 'Add freight for this job')}
                </button>
                {mfEditing && (
                  <button onClick={resetManualForm} className="text-sm text-slate-600 hover:text-slate-800">Cancel edit</button>
                )}
                {mfResult && (
                  <span role="status" aria-live="polite" className="text-sm text-green-700 inline-flex items-center gap-1">
                    <Check className="h-4 w-4" />{mfResult}
                  </span>
                )}
              </div>

              {mfError && (
                <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" aria-label="manual freight error">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{mfError}
                </div>
              )}

              {/* What is on file. The upload path has no equivalent — a C-Air figure can only be
                  replaced by another upload, whereas these are visible, editable and removable. */}
              {mfRows === null ? (
                <div className="text-xs text-slate-500">Loading…</div>
              ) : mfRows.length === 0 ? (
                <div className="text-xs text-slate-500">Nothing entered by hand yet.</div>
              ) : (
                <table className="w-full text-xs" aria-label="manual freight list">
                  <thead className="text-slate-500">
                    <tr className="text-left border-b border-slate-200">
                      <th className="py-1">Job</th><th>Ocean</th><th>Air</th><th>Reason</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {mfRows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100">
                        <td className="py-1 font-medium">{r.job_number}</td>
                        <td>{r.ocean_freight === null || r.ocean_freight === undefined ? '—' : money(r.ocean_freight)}</td>
                        <td>{r.air_freight === null || r.air_freight === undefined ? '—' : money(r.air_freight)}</td>
                        <td>{r.reason}{r.note ? `: ${r.note}` : ''}</td>
                        <td className="text-right whitespace-nowrap">
                          <button onClick={() => editManualFreight(r)} aria-label={`edit ${r.job_number}`}
                            className="text-slate-500 hover:text-teal-700 px-1"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteManualFreight(r)} aria-label={`remove ${r.job_number}`}
                            className="text-slate-500 hover:text-red-700 px-1"><Trash2 className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
